import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ElementRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { merge, of, Subscription } from 'rxjs';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { CommonService } from '../../services/common.service';
import {
  ActionIcons,
  FieldClearSuffixConstants,
} from '../../utility/constants';

/**
 * Suffix clear (X) control for mat-select and mat-autocomplete fields.
 * Place inside mat-form-field with the matSuffix attribute on this host element.
 */
@Component({
  selector: 'app-field-clear-suffix',
  standalone: true,
  imports: [SvgIconComponent, MatTooltipModule],
  templateUrl: './field-clear-suffix.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldClearSuffixComponent implements OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly commonService = inject(CommonService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  @Input() control: AbstractControl | null = null;
  @Input() multiple = false;
  @Input() emptyValue: unknown;
  @Input() autocomplete = false;
  @Input() hidden = false;
  /** Use the small close icon for compact (mat-sm) form fields. Defaults to the larger close icon. */
  @Input() large = true;
  @Input() tooltipText = FieldClearSuffixConstants.defaultTooltipText;

  @Output() cleared = new EventEmitter<void>();

  readonly actionIcons = ActionIcons;
  private controlSub?: Subscription;

  @HostBinding('style.display')
  get hostDisplay(): string | null {
    return this.showClear ? null : FieldClearSuffixConstants.hostDisplayHidden;
  }

  @HostBinding('attr.aria-hidden')
  get hostAriaHidden(): string | null {
    return this.showClear ? null : FieldClearSuffixConstants.hostAriaHiddenTrue;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes[FieldClearSuffixConstants.controlChangeKey]) {
      this.watchControl(this.control);
    }
  }

  get showClear(): boolean {
    if (this.hidden) {
      return false;
    }
    return this.commonService.hasClearableValue(this.control, this.multiple);
  }

  onClear(event: Event): void {
    event.stopPropagation();
    const input = this.elementRef.nativeElement
      .closest(FieldClearSuffixConstants.matFormFieldSelector)
      ?.querySelector(FieldClearSuffixConstants.inputSelector);

    if (this.autocomplete && input instanceof HTMLInputElement) {
      if (input.value) {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        input.value = FieldClearSuffixConstants.zeroWidthSpace;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    this.commonService.clearControlValue(this.control, {
      multiple: this.multiple,
      emptyValue: this.resolveEmptyValue(),
    });
    this.cleared.emit();
    this.cdRef.markForCheck();
  }

  private resolveEmptyValue(): unknown {
    if (this.emptyValue !== undefined) {
      return this.emptyValue;
    }
    if (this.multiple) {
      return [];
    }
    return this.autocomplete ? '' : null;
  }

  private watchControl(control: AbstractControl | null): void {
    this.controlSub?.unsubscribe();
    if (!control) {
      return;
    }
    this.controlSub = merge(
      of(control.value),
      control.valueChanges,
      control.statusChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdRef.markForCheck());
  }
}
